import React, { useState, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useKvkkRequestsApi from './config';
import { getKvkkRequest, changeKvkkRequestStatus } from '../../services/kvkkService';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import OptionsMenu from '../../components/ui/OptionsMenu';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import KvkkRequestDetails from '../../components/kvkk/KvkkRequestDetails';

// İK — KVKK Talepleri (spec §4.3). KVKK md. 11 başvurularının inceleme yüzü; otomatik veri
// işlemi YOK, İK durumu ve notu yazar, talep sahibine kapanış e-postası backend'den gider.
//
// Liste sunucu taraflıdır (config). Satır / "İncele" → DETAY UCU okunur: liste satırında
// olmayan `requesterEmailReachable` yalnız detayda gelir (backend alıcıyı kapanış
// bildirimiyle AYNI kuralla çözer). false → çekmecede belirgin uyarı: e-posta gitmeyecek,
// İK kararı yazılı iletmeli.
//
// Durum geçişi tek yerde doğrulanır (backend KvkkDataRequestRules); burada yalnız geçerli
// hedefler listelenir. Kaydetme reddinde toast'u interceptor basar, bileşen BASMAZ:
//   - 409 concurrentUpdate (iki İK aynı anda) / 400 alreadyClosed / invalidTransition /
//     404 notFound → kayıt bizim gördüğümüzden farklı: detay yeniden okunur, liste
//     yenilenir; çekmece açık kalır ve form güncel duruma göre yeniden kurulur.
//   - 400 noteRequired / noteTooLong → kullanıcının yazdığı korunur, hiçbir şey yeniden
//     yüklenmez.
//
// İK notu "formda ne görünüyorsa o"dur: mevcut not formun başlangıç değeridir ve not alanının
// değeri HER kayıtta gönderilir. Boşaltılan not boş gider; backend (Trim; boşsa null) eski
// notu siler — kapanış e-postasında da gitmez.
//
// Sıra (spec §4.3): talebin açıklaması ve damgaları önce, durum + not sonra
// (childrenPosition='top').

const Container = styled.div``;

// Backend KvkkDataRequestRules.Transitions ile aynı.
const NEXT = {
  open: ['in_review', 'completed', 'rejected'],
  in_review: ['completed', 'rejected'],
  completed: [],
  rejected: [],
};
const STALE_MESSAGES = new Set([
  'kvkk.request.alreadyClosed',
  'kvkk.request.invalidTransition',
  'kvkk.request.notFound',
]);
const NOTE_MAX = 2000; // KvkkDataRequestRules.HrNoteMaxLength

const IDLE = { state: 'idle', data: null };

export default function KvkkRequestsPage() {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  // `selected` liste satırıdır (detay gelene kadar yer tutar); `detail` detay ucunun
  // sonucu: 'idle' | 'loading' | 'ready' | 'unavailable'.
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(IDLE);
  const detailSeq = useRef(0);

  const {
    fetching, data, loadFailed, pagination, columns: baseColumns, refresh, statusLabel,
  } = useKvkkRequestsApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  // Yeniden yüklemede eski veri yerinde kalır (titreme yok); geç gelen eski yanıt
  // (başka satır açıldıysa ya da çekmece kapandıysa) yok sayılır.
  const loadDetail = useCallback((id) => {
    detailSeq.current += 1;
    const seq = detailSeq.current;
    setDetail((prev) => ({ state: 'loading', data: prev.data }));
    getKvkkRequest(id).then((res) => {
      if (seq !== detailSeq.current) return;
      const body = res?.data;
      if (body?.status && body.data) setDetail({ state: 'ready', data: body.data });
      else setDetail({ state: 'unavailable', data: null });
    });
  }, []);

  const openReview = useCallback((row) => {
    if (!row) return;
    setSelected(row);
    setDetail(IDLE);
    loadDetail(row.id);
  }, [loadDetail]);

  const closeReview = useCallback(() => {
    detailSeq.current += 1;
    setSelected(null);
    setDetail(IDLE);
  }, []);

  const current = detail.data || selected;
  const isTerminal = !!current && (NEXT[current.status] || []).length === 0;
  const editable = detail.state === 'ready' && !isTerminal;

  const objectSchema = useMemo(() => {
    const schema = [];
    if (detail.state === 'ready' && detail.data?.requesterEmailReachable === false) {
      schema.push({
        name: 'requesterUnreachable', type: 'notice', tone: 'warning', grid: 12,
        content: t('kvkkRequest.requesterUnreachable'),
      });
    }
    if (!editable) return schema;
    const targets = NEXT[detail.data.status] || [];
    schema.push(
      {
        name: 'status', label: t('kvkkRequest.newStatus'), type: 'select', grid: 12, required: true,
        options: targets.map((id) => ({ id, name: statusLabel(id) })), idKey: 'id', labelKey: 'name',
      },
      {
        name: 'hrNote', label: t('kvkkRequest.hrNote'), type: 'textarea', grid: 12, rows: 5,
        // Reddetme kararında not zorunlu (backend 400 kvkk.request.noteRequired).
        required: (formData) => formData.status === 'rejected',
        maxLength: NOTE_MAX,
        description: t('kvkkRequest.hrNoteHint'),
      },
    );
    return schema;
  }, [detail, editable, t, statusLabel]);

  // Kimlik yalnız detay değişince değişir: GenericFormDrawer formu initialValues
  // değişince sıfırlar (409 sonrası yeniden okunan detay formu güncel duruma kurar).
  const initialValues = useMemo(
    () => (editable ? { status: '', hrNote: detail.data.hrNote || '' } : null),
    [editable, detail.data],
  );

  const handleSave = useCallback(async (formData) => {
    const id = detail.data?.id;
    if (!id) return false;
    try {
      const res = await changeKvkkRequestStatus(id, { status: formData.status, hrNote: formData.hrNote ?? '' });
      if (!res?.data?.status) return false;
      refresh();
      return true;
    } catch (err) {
      const code = err?.response?.status;
      const message = err?.response?.data?.message;
      if (code === 409 || STALE_MESSAGES.has(message)) {
        loadDetail(id);
        refresh();
      }
      return false;
    }
  }, [detail.data, refresh, loadDetail]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return <OptionsMenu actions={[{ type: 'details', label: t('kvkkRequest.review'), onClick: openReview }]} row={row} />;
        },
      },
    },
  ], [baseColumns, data, t, openReview]);

  return (
    <Container>
      {loadFailed && <LoadErrorBanner message={t('kvkkRequest.unavailable')} />}

      {useMemo(() => (
        <DataTable
          title={t('kvkkRequest.hrTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={openReview}
        />
      ), [t, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange, openReview])}

      <GenericFormDrawer
        open={!!selected}
        onClose={closeReview}
        title={t('kvkkRequest.reviewTitle')}
        schema={objectSchema}
        initialValues={initialValues}
        onSave={handleSave}
        submitDisabled={!editable}
        childrenPosition="top"
      >
        {selected && (
          <>
            {detail.state === 'unavailable' && <LoadErrorBanner message={t('kvkkRequest.detailUnavailable')} />}
            <KvkkRequestDetails request={current} variant="hr" showNote={!editable} />
          </>
        )}
      </GenericFormDrawer>
    </Container>
  );
}
